import type { HandoverBody, HandoverDetail } from '@acasa/shared'

export type SectionProps = {
  protocol: HandoverDetail
  body: HandoverBody
  patch: (update: (body: HandoverBody) => HandoverBody) => void
  readOnly: boolean
  /**
   * Turns a Kleine Korrektur under way into a Wesentliche Änderung. Owned by
   * the editor page because it has to flush the autosave first and re-seed the
   * form afterwards — the server clears the signatures underneath it.
   */
  switchToMajor?: () => Promise<void>
}

/**
 * Client-side id for a newly added row.
 *
 * `crypto.randomUUID` only exists in a secure context, and this tool is very
 * likely to end up on a tablet talking to a plain-HTTP box on the office LAN —
 * where it is simply `undefined` and adding a room would throw. The fallback is
 * not cryptographically strong, and does not need to be: these ids only have to
 * be unique inside one protocol document.
 */
export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

type WithId = { id: string }

export function updateItem<T extends WithId>(list: T[], id: string, changes: Partial<T>): T[] {
  return list.map((item) => (item.id === id ? { ...item, ...changes } : item))
}

export function removeItem<T extends WithId>(list: T[], id: string): T[] {
  return list.filter((item) => item.id !== id)
}
